import {
  FileArchive,
  FileAudio,
  FileCode2,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  type LucideIcon,
} from 'lucide-react';

export interface FileTypeInfo {
  Icon: LucideIcon;
  /** Cor hex pro ícone — diferenciação visual rápida do tipo. */
  color: string;
  /** Label curto pra tooltip ou pill. */
  label: string;
}

const DEFAULT: FileTypeInfo = {
  Icon: FileText,
  color: '#0A4EE3', // brand-500
  label: 'Arquivo',
};

/**
 * Mapeia MIME type + extensão pra ícone+cor representativos.
 * Cores escolhidas pra ecoar identidade dos apps oficiais (Adobe red pra PDF,
 * Excel green, Word blue, PowerPoint orange, etc).
 */
export function fileTypeInfo(mime: string | null | undefined, filename?: string | null): FileTypeInfo {
  const m = (mime ?? '').toLowerCase();
  const ext = (filename ?? '').toLowerCase().split('.').pop() ?? '';

  // PDF
  if (m.includes('pdf') || ext === 'pdf') {
    return { Icon: FileText, color: '#E53935', label: 'PDF' };
  }
  // Planilhas (Excel, Numbers, CSV, ODS)
  if (
    m.includes('spreadsheet') ||
    m.includes('excel') ||
    m.includes('csv') ||
    ['xls', 'xlsx', 'xlsm', 'csv', 'tsv', 'numbers', 'ods'].includes(ext)
  ) {
    return { Icon: FileSpreadsheet, color: '#16A34A', label: 'Planilha' };
  }
  // Word / Pages / RTF / ODT
  if (
    m.includes('msword') ||
    m.includes('wordprocessingml') ||
    m.includes('officedocument.word') ||
    ['doc', 'docx', 'rtf', 'pages', 'odt'].includes(ext)
  ) {
    return { Icon: FileText, color: '#2563EB', label: 'Documento' };
  }
  // PowerPoint / Keynote
  if (
    m.includes('presentation') ||
    m.includes('powerpoint') ||
    ['ppt', 'pptx', 'key', 'odp'].includes(ext)
  ) {
    return { Icon: FileText, color: '#EA580C', label: 'Apresentação' };
  }
  // Arquivos compactados
  if (
    m.includes('zip') ||
    m.includes('compressed') ||
    m.includes('x-rar') ||
    m.includes('x-7z') ||
    m.includes('x-tar') ||
    m.includes('gzip') ||
    ['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(ext)
  ) {
    return { Icon: FileArchive, color: '#7C3AED', label: 'Compactado' };
  }
  // Código
  if (
    m.includes('json') ||
    m.includes('xml') ||
    m.includes('javascript') ||
    m.includes('html') ||
    [
      'json', 'xml', 'js', 'ts', 'tsx', 'jsx', 'py', 'rb', 'go', 'java', 'c', 'cpp',
      'cs', 'sh', 'sql', 'yml', 'yaml', 'html', 'css', 'scss',
    ].includes(ext)
  ) {
    return { Icon: FileCode2, color: '#0EA5E9', label: 'Código' };
  }
  // Imagem
  if (m.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'heic'].includes(ext)) {
    return { Icon: FileImage, color: '#DB2777', label: 'Imagem' };
  }
  // Áudio
  if (m.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'opus', 'm4a', 'flac'].includes(ext)) {
    return { Icon: FileAudio, color: '#9333EA', label: 'Áudio' };
  }
  // Vídeo
  if (m.startsWith('video/') || ['mp4', 'mov', 'mkv', 'avi', 'webm'].includes(ext)) {
    return { Icon: FileVideo, color: '#0891B2', label: 'Vídeo' };
  }
  // Texto puro
  if (m.startsWith('text/') || ['txt', 'md', 'log'].includes(ext)) {
    return { Icon: FileText, color: '#6B7280', label: 'Texto' };
  }

  return DEFAULT;
}
